import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

// ── QR Code renderer (pure SVG, no external lib) ───────────────────────────
// We use the TOTP uri returned by Supabase and embed it as a simple img via
// a free QR-code-as-a-service URL pattern, but to keep it offline-friendly we
// encode the URI into a data-uri via the supabase MFA totp_uri directly.
// Supabase returns a `totp_uri` we render as a qr code image using the
// built-in browser capability: <img src="…qr-generator…">
// We fall back to showing the secret key if the image fails to load.

const STEPS = ['setup', 'verify', 'backup', 'done']

export default function SecuritySettingsPage() {
  const { user } = useAuth()
  const [step, setStep] = useState('idle') // idle | setup | verify | backup | done
  const [factorId, setFactorId] = useState(null)
  const [totpUri, setTotpUri] = useState(null)
  const [secret, setSecret] = useState(null)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [totpEnabled, setTotpEnabled] = useState(user?.user_metadata?.totp_enabled ?? false)
  const [imgError, setImgError] = useState(false)

  const startSetup = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: 'totp' })
      if (err) throw err
      setFactorId(data.id)
      setTotpUri(data.totp.uri)
      setSecret(data.totp.secret)
      setStep('setup')
    } catch (e) {
      setError(e.message ?? 'Failed to start 2FA setup')
    } finally {
      setLoading(false)
    }
  }, [])

  const verifyCode = useCallback(async () => {
    if (code.length !== 6) return setError('Enter the 6-digit code from your authenticator app')
    setLoading(true)
    setError('')
    try {
      const { data: challengeData, error: cErr } = await supabase.auth.mfa.challenge({ factorId })
      if (cErr) throw cErr

      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      })
      if (vErr) throw vErr

      // Generate 8 backup codes
      const codes = Array.from({ length: 8 }, () =>
        Math.random().toString(36).slice(2, 6).toUpperCase() + '-' +
        Math.random().toString(36).slice(2, 6).toUpperCase()
      )

      // Store hashed codes via RPC (we hash client-side for demo; in prod use edge function)
      const encoder = new TextEncoder()
      const hashedCodes = await Promise.all(codes.map(async (c) => {
        const buf = await crypto.subtle.digest('SHA-256', encoder.encode(c))
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
      }))

      await supabase.from('totp_backup_codes').insert(
        hashedCodes.map((code_hash) => ({ user_id: user.id, code_hash }))
      )

      // Update profile flag
      await supabase.from('profiles').update({ totp_enabled: true }).eq('id', user.id)

      setBackupCodes(codes)
      setTotpEnabled(true)
      setStep('backup')
    } catch (e) {
      setError(e.message ?? 'Invalid code. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [code, factorId, user])

  const disable2FA = useCallback(async () => {
    if (!confirm('Disable two-factor authentication? This will remove your extra security.')) return
    setLoading(true)
    setError('')
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totpFactor = factors?.totp?.[0]
      if (totpFactor) {
        const { error: err } = await supabase.auth.mfa.unenroll({ factorId: totpFactor.id })
        if (err) throw err
      }
      await supabase.from('profiles').update({ totp_enabled: false }).eq('id', user.id)
      await supabase.from('totp_backup_codes').delete().eq('user_id', user.id)
      setTotpEnabled(false)
      setStep('idle')
    } catch (e) {
      setError(e.message ?? 'Failed to disable 2FA')
    } finally {
      setLoading(false)
    }
  }, [user])

  const qrSrc = totpUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`
    : null

  return (
    <div className="max-w-lg mx-auto px-4 py-8 page-enter">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/settings" className="text-text-muted hover:text-text-secondary">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-xl font-bold text-text-primary">Security</h1>
      </div>

      {/* Current status */}
      <div className="bg-surface rounded-2xl border border-border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-text-primary">Two-factor authentication</p>
            <p className="text-sm text-text-muted mt-0.5">
              {totpEnabled ? 'Your account is protected with TOTP.' : 'Add an extra layer of security.'}
            </p>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            totpEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-text-muted'
          }`}>
            {totpEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}

        {step === 'idle' && (
          <div className="mt-4">
            {totpEnabled ? (
              <button
                onClick={disable2FA}
                disabled={loading}
                className="text-sm text-red-500 hover:text-red-600 font-medium disabled:opacity-50"
              >
                {loading ? 'Disabling…' : 'Disable 2FA'}
              </button>
            ) : (
              <button
                onClick={startSetup}
                disabled={loading}
                className="bg-primary text-white text-sm rounded-xl px-5 py-2.5 font-semibold disabled:opacity-50"
              >
                {loading ? 'Setting up…' : 'Set up 2FA'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Setup: scan QR */}
      {step === 'setup' && (
        <div className="bg-surface rounded-2xl border border-border p-6">
          <h2 className="font-semibold text-text-primary mb-1">Scan this QR code</h2>
          <p className="text-sm text-text-muted mb-4">
            Open Google Authenticator, Authy, or any TOTP app and scan the code below.
          </p>

          <div className="flex justify-center mb-4">
            {!imgError && qrSrc ? (
              <img
                src={qrSrc}
                alt="TOTP QR code"
                className="rounded-lg border border-border"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="bg-gray-50 rounded-lg border border-border p-4 text-center">
                <p className="text-xs text-text-muted mb-2">Enter this code manually:</p>
                <p className="font-mono text-sm font-bold text-text-primary tracking-widest break-all">{secret}</p>
              </div>
            )}
          </div>

          {secret && !imgError && (
            <p className="text-xs text-text-muted text-center mb-4">
              Can't scan?{' '}
              <button className="underline" onClick={() => setImgError(true)}>
                Enter code manually
              </button>
            </p>
          )}

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-text-primary block mb-1.5">
                Verification code
              </span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full border border-border rounded-xl px-4 py-3 text-center text-xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </label>
            <button
              onClick={verifyCode}
              disabled={loading || code.length !== 6}
              className="w-full bg-primary text-white rounded-xl py-3 font-semibold text-sm disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify and enable'}
            </button>
          </div>
        </div>
      )}

      {/* Backup codes */}
      {step === 'backup' && (
        <div className="bg-surface rounded-2xl border border-border p-6">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <h2 className="font-semibold text-text-primary">Save your backup codes</h2>
          </div>
          <p className="text-sm text-text-muted mb-4">
            Store these codes somewhere safe. Each can be used once if you lose access to your authenticator app.
          </p>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {backupCodes.map((c) => (
              <code key={c} className="bg-gray-50 border border-border rounded-lg px-3 py-2 text-sm font-mono text-center text-text-primary">
                {c}
              </code>
            ))}
          </div>

          <button
            onClick={() => {
              const text = backupCodes.join('\n')
              navigator.clipboard?.writeText(text)
            }}
            className="w-full border border-border rounded-xl py-2.5 text-sm font-medium text-text-secondary hover:bg-gray-50 mb-3"
          >
            Copy all codes
          </button>

          <button
            onClick={() => setStep('done')}
            className="w-full bg-primary text-white rounded-xl py-3 font-semibold text-sm"
          >
            I've saved my codes
          </button>
        </div>
      )}

      {step === 'done' && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
          <svg className="w-10 h-10 text-green-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="font-semibold text-green-800 mb-1">2FA is now active</h2>
          <p className="text-sm text-green-700">You'll be asked for a code each time you sign in.</p>
          <Link
            to="/settings"
            className="mt-4 inline-block text-sm text-green-700 underline underline-offset-2"
          >
            Back to settings
          </Link>
        </div>
      )}
    </div>
  )
}
